import { useEffect, useState } from "react";
import api from "../lib/api";
import { getCachedSiteContent } from "../lib/useSiteContent";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { trackEvent } from "../components/GAScript";
import SeoPageHead from "../components/SeoPageHead";

/**
 * Standalone waitlist landing page.
 *
 * Replaces the public homepage entirely when `site_content.waitlist_landing_enabled`
 * is "true". Content is fully CMS-driven via the `wl_*` keys (see Admin →
 * Waitlist tab in the sidebar).
 *
 * Visual reference: Pakistan-themed minimal hero with Pakistan landmarks
 * illustration, three value pills (Share / Create / Get rewarded), and a
 * single name + email collection form.
 */
export default function WaitlistLanding() {
  // Render NOTHING until the CMS content has resolved — otherwise admins
  // who edited copy will see the placeholder defaults flash before their
  // edits paint. We show a tiny inline splash (same cream bg as the page
  // body) so the eventual swap to real content is visually invisible.
  const [cms, setCms] = useState(() => {
    const c = getCachedSiteContent();
    return Object.keys(c).length > 0 ? c : null;
  });
  useEffect(() => {
    api.get("/site-content")
      .then((r) => setCms(r.data || {}))
      .catch(() => setCms({})); // network failure — fall through to defaults
  }, []);

  const t = (k, fb = "") => (cms?.[k] ?? fb);

  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // null | 'new' | 'existing'

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email");
      return;
    }
    setSubmitting(true);
    try {
      const url = new URL(window.location.href);
      const { data } = await api.post("/waitlist/join", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        referral_code: url.searchParams.get("ref") || null,
        utm_source: url.searchParams.get("utm_source") || "wl_landing",
        utm_campaign: url.searchParams.get("utm_campaign") || null,
        utm_medium: url.searchParams.get("utm_medium") || null,
      });
      if (data.already) {
        setDone("existing");
      } else {
        setDone("new");
        trackEvent("waitlist_join", { surface: "wl_landing" });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't join right now — try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  // Hold render until CMS data arrives — prevents the "default copy then
  // your edits swap in" flash on every visit. Same cream-bg splash so the
  // swap is visually invisible.
  if (cms === null) {
    return (
      <div
        style={{
          minHeight: "100vh", display: "flex",
          alignItems: "center", justifyContent: "center",
          background: "#F6F1E8", color: "#0a0a0a",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
        data-testid="waitlist-landing-loading"
        aria-busy="true"
      >
        <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em", opacity: 0.85 }}>
          oodel<span style={{ color: "#16A34A" }}>.</span>
        </div>
      </div>
    );
  }

  const heroImage = t("wl_hero_image_url", "https://customer-assets.emergentagent.com/job_pk-poll-platform/artifacts/gxwc937r_2.png");
  const brand = t("brand_name", "oodel.");

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: t("wl_bg_color", "#F6F1E8") }} data-testid="waitlist-landing">
      <SeoPageHead
        pageKey="home"
        path="/"
        defaultTitle={t("wl_meta_title", "oodel. — Real issues. Real people. Real impact.")}
        defaultDescription={t("wl_meta_description", "Pakistan's first nationally-representative citizen voice panel. Join the waitlist.")}
      />

      {/* Tiny top bar — just the wordmark, Google-style minimal */}
      <header className="px-6 md:px-12 py-6 flex items-center justify-between">
        <div className="flex items-baseline gap-1" data-testid="wl-wordmark">
          <span className="text-2xl md:text-3xl font-black tracking-tight text-black">{brand.replace(/\.$/, "")}</span>
          <span className="text-2xl md:text-3xl font-black text-[#34A853]">.</span>
        </div>
        <div className="text-xs text-neutral-500 hidden md:block">{t("wl_topbar_tag", "Pakistan's citizen voice panel")}</div>
      </header>

      {/* Hero — two-column layout, illustration right, headline+form left */}
      <main className="flex-1 px-6 md:px-12 py-6 md:py-12">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-12 items-center">
          {/* Left: words + form */}
          <div>
            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-black"
              data-testid="wl-headline"
            >
              {(t("wl_h1_line1", "Real issues.") + "\n" + t("wl_h1_line2", "Real people.") + "\n" + t("wl_h1_line3", "Real impact.")).split("\n").map((line, i) => (
                <span key={i} className="block">
                  {renderHighlighted(line, "#2E8B57")}
                </span>
              ))}
            </h1>

            <div className="mt-6 h-1.5 w-24 bg-mango rounded-full" />

            <p className="mt-6 text-lg md:text-xl text-neutral-700 max-w-lg" data-testid="wl-sub">
              {renderHighlighted(t("wl_sub", "Your voice can turn challenges into real change."), "#2E8B57")}
            </p>

            {/* Three value pills */}
            <div className="mt-8 flex flex-wrap gap-3" data-testid="wl-pills">
              {[
                { label: t("wl_pill_1", "Share your opinion."), icon: "💬" },
                { label: t("wl_pill_2", "Create impact."), icon: "👥" },
                { label: t("wl_pill_3", "Get rewarded for it!"), icon: "🎁" },
              ].map((p) => (
                <div key={p.label} className="bg-white/60 backdrop-blur border border-[#2E8B57]/15 rounded-full px-4 py-2 flex items-center gap-2 text-sm">
                  <span className="text-base">{p.icon}</span>
                  <span className="font-medium text-neutral-800">{renderHighlighted(p.label, "#2E8B57")}</span>
                </div>
              ))}
            </div>

            {/* Form */}
            {done ? (
              <div className="mt-10 max-w-md bg-white border-2 border-[#2E8B57] p-6 hard-shadow" data-testid="wl-success">
                <div className="flex items-center gap-2 text-[#2E8B57] font-bold mb-2">
                  <Check size={18} /> {done === "existing" ? t("wl_already_title", "You're already in!") : t("wl_success_title", "You're on the list!")}
                </div>
                <p className="text-sm text-neutral-700">
                  {done === "existing"
                    ? t("wl_already_body", "Looks like you joined us earlier. Watch your inbox — we'll email you the moment surveys go live in your area.")
                    : t("wl_success_body", "We'll email you when surveys go live. In the meantime, share the link with friends — the bigger the panel, the more representative the picture.")}
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-10 max-w-2xl" data-testid="wl-form">
                <div className="bg-white border-2 border-black rounded-full px-2 py-1.5 flex items-center gap-1 hard-shadow" data-testid="wl-form-shell">
                  <input
                    data-testid="wl-name"
                    name="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t("wl_placeholder_name", "Your name")}
                    required
                    autoComplete="name"
                    className="flex-[1.1] min-w-0 px-5 py-2.5 outline-none text-base bg-transparent placeholder:text-neutral-400 border-r border-neutral-200"
                  />
                  <input
                    data-testid="wl-email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={t("wl_placeholder_email", "you@example.com")}
                    required
                    autoComplete="email"
                    className="flex-[1.4] min-w-0 px-5 py-2.5 outline-none text-base bg-transparent placeholder:text-neutral-400"
                  />
                  <button
                    type="submit"
                    data-testid="wl-submit"
                    disabled={submitting}
                    className="shrink-0 bg-black hover:bg-neutral-800 active:scale-95 transition text-white rounded-full px-6 py-2.5 font-bold text-sm tracking-wide disabled:opacity-50 inline-flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {submitting ? <><Loader2 size={16} className="animate-spin" /> Joining…</> : <>{t("wl_cta", "Join the waitlist")} <span aria-hidden>→</span></>}
                  </button>
                </div>
                <p className="mt-3 text-xs text-neutral-500 px-1">
                  {t("wl_fineprint", "We'll only email you about oodel — no spam, no resold lists. Unsubscribe any time.")}
                </p>
              </form>
            )}
          </div>

          {/* Right: hero illustration */}
          <div className="relative flex items-center justify-center" data-testid="wl-illustration">
            <img
              src={heroImage}
              alt={t("wl_hero_image_alt", "Pakistanis sharing their opinions")}
              className="w-full max-w-[560px] h-auto select-none"
              draggable={false}
            />
          </div>
        </div>

        {/* Footer strap with second message */}
        <div className="max-w-[1200px] mx-auto mt-12 md:mt-20 border-t-2 border-black/15 pt-8" data-testid="wl-strap">
          <p className="text-2xl md:text-3xl font-bold tracking-tight text-black max-w-3xl">
            {renderHighlighted(t("wl_strap", "The future isn't decided in boardrooms alone. It's shaped by people like you."), "#2E8B57")}
          </p>
        </div>
      </main>

      <footer className="px-6 md:px-12 py-6 text-xs text-neutral-500 flex flex-wrap items-center justify-between gap-3">
        <span>{t("wl_footer_left", "© oodel. — Built in Pakistan, for Pakistan.")}</span>
        <span className="font-mono">{t("wl_footer_right", "Together, let's build a better tomorrow.")}</span>
      </footer>
    </div>
  );
}

/**
 * Renders a string with simple ASCII highlighting: any word wrapped in
 * `*word*` (single asterisks) is rendered with the brand-green colour. Lets
 * the admin author copy like `Real *issues*. Real *people*.` directly from
 * the CMS without needing HTML.
 */
function renderHighlighted(text, color) {
  if (!text) return null;
  const parts = String(text).split(/(\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("*") && p.endsWith("*")) {
      return <span key={i} style={{ color }}>{p.slice(1, -1)}</span>;
    }
    return <span key={i}>{p}</span>;
  });
}
